import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime } from '../../i18n';
import { useSession } from '../auth/queries';
import { useTenantSettings, useUpdateTenantSettings } from '../privacy/tenantQueries';
import { useSendSurveysNow, useSurveySummary } from './queries';

/**
 * Was an Umfragen noch aussteht -- F5-10.
 *
 * **Nur die unbeantworteten.** Wer geantwortet hat, steht im Bericht
 * "Kundenzufriedenheit" mit Mittelwert und Grundlage; hier steht der Rest.
 * Beides zusammen ist die ganze Aussage: Ein Mittelwert von 4,8 aus zwei
 * Antworten bei achtzig Befragten ist kein Mittelwert, sondern zwei
 * freundliche Menschen.
 *
 * **Kein Name und keine Ticketnummer.** Die Antwort des Servers traegt
 * Kennungen und keine Beschriftungen; sie nachzuschlagen hiesse, fuer jede
 * offene Umfrage einen Kunden und ein Ticket zu laden -- fuer eine Liste,
 * aus der niemand etwas anklickt. Die Zahl und das Alter der aeltesten
 * beantworten die Frage, die hier gestellt wird: Kommt ueberhaupt etwas
 * zurueck?
 *
 * **Der Schalter steht seit F5-10a hier.** Er lag in ``tenants.features``
 * und war aus -- mit gutem Grund, denn eine Umfrage geht an Kunden hinaus.
 * Nur gab es keinen Weg, ihn umzulegen: keine Maske, kein Knopf, nur ein
 * Feld in der Datenbank. Wer die Umfragen einschalten wollte, musste die
 * Schnittstelle von Hand aufrufen. Ein Schalter, den man nicht findet, ist
 * fuer den Betreiber dasselbe wie eine Funktion, die es nicht gibt.
 *
 * **Wartend und verschickt stehen getrennt.** Die Uebersicht zaehlte alle
 * unbeantworteten Umfragen als "verschickt" -- auch die, die noch auf ihren
 * Versand warteten. Wer am Tag des Abschlusses nachsah, fand dort eine
 * "verschickte" Umfrage und im Postfach des Kunden keine.
 *
 * **"Jetzt versenden" wartet nicht.** Der Knopf rief denselben Lauf wie der
 * Hintergrunddienst und verschickte deshalb nur, was ohnehin faellig war --
 * am Tag des Abschlusses also nichts. Jetzt verschickt er alles, was wartet.
 *
 * **Verzoegerung und Anteil lassen sich hier einstellen.** Sie standen nur
 * in der Datenbank. Die Vorgaben kommen vom Server (``feature_defaults``);
 * die Oberflaeche kennt keine eigenen Zahlen -- zwei Stellen mit derselben
 * Vorgabe hatten schon einmal verschiedene Werte.
 */
export function SurveyPanel() {
  const { t, i18n } = useTranslation();
  const summary = useSurveySummary();
  const send = useSendSurveysNow();

  const session = useSession();
  const settings = useTenantSettings();
  const update = useUpdateTenantSettings();

  // Einstellen darf, wer die Stammdaten des Mandanten fuehrt -- dasselbe
  // Recht, das der Endpunkt verlangt. Ohne diese Abfrage saehe jeder
  // Berichtsleser einen Schalter, der ihm mit 403 antwortet.
  const mayConfigure = (session.data?.permissions ?? []).includes('masterdata.write');
  const enabled = settings.data?.features.satisfaction_surveys === true;

  if (summary.isError) return <ErrorNotice error={summary.error} />;

  const stand = summary.data;

  return (
    <section className="card stack">
      <div className="chart-card__head">
        <h2>{t('surveys.title')}</h2>
        <p className="chart-card__hint">{t('surveys.hint')}</p>
      </div>

      {mayConfigure ? (
        <div className="field">
          <label className="field__checkbox">
            <input
              type="checkbox"
              checked={enabled}
              disabled={settings.isPending || update.isPending}
              onChange={(event) => {
                update.mutate({ features: { satisfaction_surveys: event.target.checked } });
              }}
            />
            {t('surveys.enable')}
          </label>
          <p className="field__hint">
            {enabled ? t('surveys.enabledHint') : t('surveys.disabledHint')}
          </p>
          {update.isError ? <ErrorNotice error={update.error} /> : null}
        </div>
      ) : null}

      {mayConfigure && enabled && settings.data ? (
        <SurveySettings
          // Neu aufgebaut, sobald der Server andere Werte meldet -- sonst
          // stuenden nach dem Speichern die alten Eingaben im Feld.
          key={`${String(settings.data.features.survey_delay_hours)}-${String(
            settings.data.features.survey_quota_percent,
          )}`}
          delay={effective(settings.data, 'survey_delay_hours')}
          quota={effective(settings.data, 'survey_quota_percent')}
        />
      ) : null}

      {stand ? (
        <div className="kpi-grid">
          <Figure
            label={t('surveys.waiting')}
            value={stand.waiting}
            hint={
              stand.next_due_at
                ? t('surveys.nextDue', {
                    moment: formatDateTime(stand.next_due_at, i18n.language),
                  })
                : t('surveys.nothingWaiting')
            }
          />
          <Figure
            label={t('surveys.sentUnanswered')}
            value={stand.sent_unanswered}
            hint={t('surveys.sentUnansweredHint')}
          />
          <Figure
            label={t('surveys.answered')}
            value={stand.answered}
            hint={
              stand.response_rate_percent === null
                ? t('surveys.noResponseRate')
                : t('surveys.responseRate', { percent: stand.response_rate_percent })
            }
          />
        </div>
      ) : null}

      {mayConfigure ? (
        <div className="row">
          <Button
            variant="secondary"
            busy={send.isPending}
            disabled={stand?.waiting === 0}
            onClick={() => {
              send.mutate();
            }}
          >
            {t('surveys.sendNow')}
          </Button>
          {send.data ? (
            <span className="field__hint" role="status">
              {t('surveys.sendResult', { count: send.data.sent })}
            </span>
          ) : (
            <span className="field__hint">{t('surveys.sendNowHint')}</span>
          )}
        </div>
      ) : null}

      {send.isError ? <ErrorNotice error={send.error} /> : null}
    </section>
  );
}

type TenantSettings = NonNullable<ReturnType<typeof useTenantSettings>['data']>;

/** Der wirksame Wert: eingestellt, sonst die Vorgabe des Servers. */
function effective(settings: TenantSettings, key: string): number {
  const value = settings.features[key] ?? settings.feature_defaults[key];
  return typeof value === 'number' ? value : 0;
}

function Figure({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="kpi">
      <p className="kpi__label">{label}</p>
      <p className="kpi__value">{value}</p>
      <p className="table__muted">{hint}</p>
    </div>
  );
}

/**
 * Verzoegerung und Anteil -- F5-10.
 *
 * Mit eigenem Speichernknopf und nicht bei jedem Tastendruck: "2" auf dem
 * Weg zu "24" waere sonst fuer einen Augenblick eine gueltige Einstellung --
 * und eine Umfrage, die in genau diesem Augenblick entsteht, ginge nach zwei
 * Stunden hinaus.
 */
function SurveySettings({ delay, quota }: { delay: number; quota: number }) {
  const { t } = useTranslation();
  const update = useUpdateTenantSettings();
  const [delayInput, setDelayInput] = useState(String(delay));
  const [quotaInput, setQuotaInput] = useState(String(quota));

  const delayValue = Number(delayInput);
  const quotaValue = Number(quotaInput);
  const valid =
    Number.isInteger(delayValue) &&
    delayValue >= 0 &&
    delayValue <= 720 &&
    Number.isInteger(quotaValue) &&
    quotaValue >= 0 &&
    quotaValue <= 100;
  const changed = delayValue !== delay || quotaValue !== quota;

  return (
    <div className="stack">
      {/* Oben ausgerichtet: Die Hinweise darunter sind verschieden lang, und
          mittig ausgerichtet standen die beiden Felder versetzt. */}
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="field">
          <label className="field__label" htmlFor="survey-delay">
            {t('surveys.delay')}
          </label>
          <input
            id="survey-delay"
            type="number"
            min={0}
            max={720}
            className="field__control"
            value={delayInput}
            onChange={(event) => {
              setDelayInput(event.target.value);
            }}
          />
          <span className="field__hint">{t('surveys.delayHint')}</span>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="survey-quota">
            {t('surveys.quota')}
          </label>
          <input
            id="survey-quota"
            type="number"
            min={0}
            max={100}
            className="field__control"
            value={quotaInput}
            onChange={(event) => {
              setQuotaInput(event.target.value);
            }}
          />
          <span className="field__hint">{t('surveys.quotaHint')}</span>
        </div>
      </div>
      {!valid ? (
        <span className="field__error" role="alert">
          {t('surveys.settingsInvalid')}
        </span>
      ) : null}
      {update.isError ? <ErrorNotice error={update.error} /> : null}
      <div className="row">
        <Button
          variant="secondary"
          busy={update.isPending}
          disabled={!valid || !changed}
          onClick={() => {
            update.mutate({
              features: { survey_delay_hours: delayValue, survey_quota_percent: quotaValue },
            });
          }}
        >
          {t('app.save')}
        </Button>
      </div>
    </div>
  );
}
